import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import useScrollTop from "@src/lib/hooks/useScrollTop";
import { FC, ReactNode, useMemo } from "react";
import React from "react";
import { useRouter } from "next/router";
import { UserType } from "@src/types.generated";
import useUser from "@src/features/app/hooks/useUser";
import NotFound from "@src/pages/404";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { SidebarInset, SidebarProvider } from "@src/lib/ui/Sidebar";
import LeftNav from "@src/features/app/left-nav/LeftNav";
import TopNav from "@src/features/app/top-nav/TopNav";
import PageContentHeaderZone from "@src/features/app/top-nav/PageContentHeaderZone";
import PageContentHeaderZoneV2 from "@src/features/app/top-nav/PageContentHeaderZoneV2";
import { isBuilderView } from "@src/features/app/top-nav/builderNav";
import { KnowledgeBaseChat } from "@src/features/assistantChat/KnowledgeBaseChat";
import { OpusAgentChat } from "@src/features/opus-agent/OpusAgentChat";
import {
  isChatJoined,
  resolveChatSurface,
} from "@src/features/app/chatSurface";
import { pageContentOverflowStyle } from "@src/features/app/pageContentOverflow";
import useIsFullAdmin from "@src/features/app/hooks/useIsFullAdmin";
import {
  useFeatureFlag,
  useFeatureFlagsLoaded,
} from "@src/features/app/hooks/useFeatureFlag";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { AiChatType, Ff } from "@src/types.generated";
import { UserActionPermissionBooleans } from "@src/lib/contexts/ActionPermissionsContext";
import { cn } from "@src/lib/utils/cn";

export type ActionPermissionString = keyof UserActionPermissionBooleans;
type Props = {
  children: ReactNode;
  showTopNav?: boolean;
  showLeftNav?: boolean;
  defaultSidebarOpen?: boolean;
  overflow?: React.CSSProperties["overflow"];
  dataTestId?: string;
  forbiddenUserTypes: UserType[];
  requiredActionPermissions: ActionPermissionString[];
  hideKnowledgeBaseChat?: boolean;
  forceKnowledgeBaseChat?: boolean;
};

export const Page: FC<Props> = ({
  children,
  showTopNav = true,
  showLeftNav = true,
  defaultSidebarOpen = true,
  overflow = "auto",
  dataTestId,
  forbiddenUserTypes,
  requiredActionPermissions,
  hideKnowledgeBaseChat = false,
  forceKnowledgeBaseChat = false,
}: Props) => {
  const { scrollProps } = useScrollTop();
  const { user } = useUser();
  const router = useRouter();
  const chatKnowledgeBaseEnabled = useFeatureFlag(Ff.ChatKnowledgeBase);
  const opusAgentEnabled = useFeatureFlag(Ff.OpusAgent);
  const libraryItemOverviewTabEnabled = useFeatureFlag(
    Ff.LibraryItemOverviewTab,
  );
  const flagsResolved = useFeatureFlagsLoaded();
  const isFullAdmin = useIsFullAdmin();
  const { isOpen: isKnowledgeBaseChatOpen } = useChatSidebar(
    AiChatType.KnowledgeBase,
  );

  const hideTopNavView =
    isBuilderView(router.pathname, router.query) ||
    router.pathname === "/path-builder";
  const showTopNavBar = showTopNav && !hideTopNavView;

  const LeftNavComponent = useMemo(() => {
    if (!showLeftNav) return null;
    return <LeftNav />;
  }, [showLeftNav]);

  const { userActionPermissions, loading } = useActionPermissions();
  const hasPermission = useMemo(() => {
    if (loading || !user?.userType) {
      // avoid a 404 flash
      return true;
    }
    if (user?.userType && forbiddenUserTypes.includes(user.userType)) {
      return false;
    }
    if (userActionPermissions) {
      return requiredActionPermissions.every((ap) => userActionPermissions[ap]);
    }
    return false;
  }, [
    userActionPermissions,
    forbiddenUserTypes,
    requiredActionPermissions,
    loading,
    user?.userType,
  ]);

  if (!hasPermission) {
    return <NotFound />;
  }
  const shouldShowChat =
    showTopNav &&
    (chatKnowledgeBaseEnabled || forceKnowledgeBaseChat) &&
    !hideKnowledgeBaseChat;
  const chatSurface = resolveChatSurface({
    flagsResolved,
    // The same "user still loading" signal hasPermission uses above.
    userResolved: !!user?.userType,
    opusAgentEnabled,
    isFullAdmin,
  });
  const chatJoined = isChatJoined({
    shouldShowChat,
    chatSurface,
    chatOpen: isKnowledgeBaseChatOpen,
  });

  return (
    <SidebarProvider
      defaultOpen={defaultSidebarOpen}
      className="h-svh max-h-svh w-screen min-w-0 overflow-x-hidden bg-[#f9f8f6]"
      style={
        {
          "--sidebar-width": "248px",
          "--admin-primary-rail-collapsed-width": "56px",
          "--admin-primary-rail-expanded-width": "200px",
          "--admin-secondary-menu-width": "220px",
          overflow,
        } as React.CSSProperties
      }
    >
      <div
        className="relative flex min-h-0 min-w-0 flex-1 flex-col"
        data-testid={dataTestId}
      >
        <div className="flex min-h-0 min-w-0 flex-1 self-stretch">
          {LeftNavComponent}
          <SidebarInset
            className={cn(
              "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-out-sm md:my-2 md:ml-0",
              chatJoined ? "rounded-r-none" : "md:mr-2",
            )}
          >
            {showTopNavBar ? <TopNav /> : null}
            {showTopNav ? (
              <>
                <div
                  id="top-nav-banner"
                  className="flex w-full min-w-0 shrink-0 flex-col empty:hidden"
                />
                {libraryItemOverviewTabEnabled ? (
                  <PageContentHeaderZoneV2 />
                ) : (
                  <PageContentHeaderZone />
                )}
                <div
                  id="top-nav-subheader"
                  className="flex min-h-10 w-full min-w-0 shrink-0 items-center justify-between gap-3 border-b border-gray-2 px-3 empty:hidden md:px-4"
                />
                <div
                  id="top-nav-tertiary"
                  className="flex min-h-10 w-full min-w-0 shrink-0 items-center gap-2 px-2 empty:hidden md:px-4"
                />
              </>
            ) : null}
            <ScrollableFlexGrow
              flex={1}
              alignSelf={"stretch"}
              innerStyle={{
                alignItems: "center",
              }}
              onScroll={scrollProps.onScroll}
            >
              <div
                id={"page-content-inner"}
                {...scrollProps}
                className="flex flex-1 self-stretch"
                style={pageContentOverflowStyle(overflow)}
              >
                {children}
              </div>
            </ScrollableFlexGrow>
          </SidebarInset>
          {shouldShowChat && chatSurface === "opus-agent" && <OpusAgentChat />}
          {shouldShowChat && chatSurface === "knowledge-base" && (
            <KnowledgeBaseChat />
          )}
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Page;
